import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { openPrintWindow } from "../../utils/printReport";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: "80vw",
    maxWidth: "90vw",
    minHeight: "70vh",
    maxHeight: "90vh",
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  tableContainer: {
    maxHeight: "60vh",
    marginTop: theme.spacing(2),
  },
  headerCell: {
    backgroundColor: theme.palette.grey[200],
    fontWeight: "bold",
    fontSize: "0.875rem",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  cell: {
    fontSize: "0.875rem",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  clienteName: {
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
  motivoChip: {
    margin: theme.spacing(0.25),
    fontSize: "0.75rem",
  },
  observacoes: {
    maxWidth: 300,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const UsageReportDetailsModal = ({ 
  open, 
  onClose, 
  details, 
  loading, 
  userName, 
  detailTypeName,
  period 
}) => {
  const classes = useStyles();

  const fmt = (date) => {
    try { return date ? format(new Date(date), "dd/MM/yyyy", { locale: ptBR }) : ""; }
    catch { return ""; }
  };

  const handlePrint = () => {
    const headers = [
      i18n.t("usageReport.details.contactDate"),
      i18n.t("usageReport.details.client"),
      i18n.t("usageReport.details.contactMethodFull"),
      i18n.t("usageReport.details.result"),
      i18n.t("common.notes"),
      i18n.t("usageReport.details.nextAction"),
      i18n.t("usageReport.details.nextActionDate"),
      i18n.t("usageReport.details.reasons")
    ];
    const rows = (details || []).map(d => [
      fmt(d.data_contato),
      `${d.clienteNome} (ID: ${d.clienteId})`,
      d.meio_contato || "",
      d.resultado_visita || "",
      d.observacoes || "",
      d.proxima_acao || "",
      fmt(d.data_proxima_acao),
      (d.motivos || []).join(", ")
    ]);
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    openPrintWindow(
      `${detailTypeName} — ${userName}`,
      i18n.t("usageReport.modal.printSubtitle", { period, total: (details || []).length, date: now }),
      headers,
      rows
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";
    try {
      return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return "-";
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "-";
    try {
      return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return "-";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">
          {i18n.t("usageReport.modal.title", { user: userName, type: detailTypeName })}
        </Typography>
        {period && (
          <Typography variant="subtitle2">
            {i18n.t("usageReport.periodLabel", { period })}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : details && details.length > 0 ? (
          <>
            <Typography variant="subtitle1" gutterBottom>
              {i18n.t("usageReport.modal.totalRecords")} <strong>{details.length}</strong>
            </Typography>
            
            <TableContainer className={classes.tableContainer} component={Paper}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>{i18n.t("usageReport.details.contactDate")}</TableCell>
                    <TableCell className={classes.headerCell}>{i18n.t("usageReport.details.client")}</TableCell>
                    <TableCell className={classes.headerCell}>{i18n.t("usageReport.details.contactMethod")}</TableCell>
                    <TableCell className={classes.headerCell}>{i18n.t("usageReport.details.result")}</TableCell>
                    <TableCell className={classes.headerCell}>{i18n.t("common.notes")}</TableCell>
                    <TableCell className={classes.headerCell}>{i18n.t("usageReport.details.nextAction")}</TableCell>
                    <TableCell className={classes.headerCell}>{i18n.t("usageReport.details.nextActionDate")}</TableCell>
                    <TableCell className={classes.headerCell}>{i18n.t("usageReport.details.reasons")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell className={classes.cell}>
                        {formatDateTime(item.data_contato)}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <span className={classes.clienteName}>
                          {item.clienteNome}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {item.meio_contato || "-"}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {item.resultado_visita || "-"}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Tooltip
                          title={item.observacoes || "-"}
                          placement="top"
                          arrow
                        >
                          <div className={classes.observacoes}>
                            {item.observacoes || "-"}
                          </div>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {item.proxima_acao || "-"}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {formatDate(item.data_proxima_acao)}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {item.motivos && item.motivos.length > 0 ? (
                          item.motivos.map((motivo, idx) => (
                            <Chip
                              key={idx}
                              label={motivo}
                              size="small"
                              color="primary"
                              variant="outlined"
                              className={classes.motivoChip}
                            />
                          ))
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant="h6">
              {i18n.t("common.noRecords")}
            </Typography>
            <Typography variant="body2">
              {i18n.t("usageReport.modal.emptyText")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handlePrint}
          color="primary"
          variant="outlined"
          disabled={loading || !details?.length}
        >
          {i18n.t("common.print")}
        </Button>
        <Button onClick={onClose} color="primary" variant="contained">
          {i18n.t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsageReportDetailsModal;
